import * as React from "react";

// Material-UI
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import ClearIcon from '@mui/icons-material/Clear';
import Grid from '@mui/material/Grid';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import EmailFormHelpDialog from './EmailFormHelpDialog';
import RichTextEditor from '../../shared/RichTextEditor';

import {
  EmailAttachment,
  EmailMessage,
  LodgeUseEmailMessage
} from '../../models';

import {
  API_URL,
  EMPTY_STRING
} from '../../global';

import {
  HTTPHeaders,
  RichTextEditorRef
} from '../../interfaces';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

interface EmailProps {
  ids: Array<number> //MemberSignupIDs for email
}

const EmailForm: React.FC<EmailProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { enqueueSnackbar } = useSnackbar();

  const [emailMessage, setEmailMessage] = React.useState<EmailMessage>(new EmailMessage());
  const [helpOpen, setHelpOpen] = React.useState<boolean>(false);
  const [sendDisabled, setSendDisabled] = React.useState<boolean>(true);
  const [working, setWorking] = React.useState<boolean>(false);

  // Ensures the editor only sets the starting text once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialContent = React.useMemo(() => emailMessage.htmlContent, []);

  // Reference objects
  const editorRef = React.useRef<RichTextEditorRef>(null);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      width: 600
    },
    attachmentsHeading: {
      paddingTop: theme.spacing(1)
    },
    attachmentListItem: {
      margin: theme.spacing(1, 0, 0, 0)
    },
    attachmentListItemText: {
      padding: theme.spacing(0, 0, 0, 1)
    },
    editor: {
      margin: theme.spacing(1, 1, 1, 1)
    },
    fileUpload: {
      display: 'none'
    },
    subject: {
      flexGrow: 1
      , margin: theme.spacing(1, 1, 1, 1)
    }
  };

  // #endregion

  // #region Methods

  /*
   * Reset email fields
   */
  const clearEmail = () => {
    // 1. Reset the local state object containing the email details (subject, body, etc.)
    setEmailMessage(new EmailMessage());

    // 2. Access the child component's imperative 'reset' method via the ref.
    // This clears the TinyMCE editor content, the attachment list, and the file input.
    editorRef.current?.reset();
  }

  /**
   * Processes and sends the lodge use email to the selected members.
   * Maps the current editor state and attachments into a flattened email model
   * before dispatching the POST request to the API.
   */
  const sendEmail = async () => {
    // 1. Initiate the 'working' state to display a loading indicator and lock the UI.
    setWorking(true);

    // 2. Obtain the Auth0 Bearer token for API authorization.
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    };

    const url = new URL(`${API_URL}/v1/email/lodgeuse`);

    // 3. Prepare the email payload using the LodgeUseEmailMessage model.
    const lodgeUseEmailMessage: LodgeUseEmailMessage = new LodgeUseEmailMessage();

    // 4. Convert the array of member IDs into a comma-delimited string for the backend.
    lodgeUseEmailMessage.ids = props.ids.join(',');
    lodgeUseEmailMessage.subject = emailMessage.subject;
    lodgeUseEmailMessage.htmlContent = emailMessage.htmlContent;

    // 5. Retrieve attachments directly from the Rich Text Editor instance.
    const emailAttachments = editorRef.current?.getAttachments();

    // 6. If attachments exist, transform the list of objects into a comma-separated 
    // string of unique IDs (e.g., "101,102,105") required by the API.
    if (emailAttachments && emailAttachments.length > 0) {
      let attachments: string = EMPTY_STRING;
      emailAttachments.forEach((ea: EmailAttachment) => {
        attachments += `${attachments ? ',' : EMPTY_STRING}${ea.emailAttachmentID}`;
      });
      lodgeUseEmailMessage.attachments = attachments;
    }

    // 7. Execute the POST request to the lodge use email endpoint.
    await fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify(lodgeUseEmailMessage),
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        // 8. Handle non-success HTTP status codes (4xx/5xx).
        if (!response.ok) {
          enqueueSnackbar(`WARNING: Email was not sent to members`, { variant: 'warning' });
        } else {
          // 9. On success: Notify the user, clear the form, and reset local state.
          enqueueSnackbar(`Email sent to selected members`, { variant: 'success' });
          clearEmail();
        }
        // 10. Turn off the loading indicator.
        setWorking(false);
      })
      .catch(error => {
        // 11. Catch network failures or exceptions during the request process.
        setWorking(false);
        enqueueSnackbar(`WARNING: Email was not sent to members, Error: ${error}`, { variant: 'error' });
      });
  };

  /**
   * Evaluates the email message for minimum required data.
   * Toggles the 'Send' button state based on the presence of recipients,
   * a subject line, and the HTML body content.
   */
  const validateEmail = () => {
    // Check that all mandatory email components are present:
    if (
      // 1. Ensure at least one recipient ID has been passed via props.
      props.ids.length > 0 &&

      // 2. Ensure the subject line is not empty or null.
      emailMessage.subject &&

      // 3. Ensure the rich text editor has generated HTML content.
      emailMessage.htmlContent
    ) {
      // All requirements met: Enable the 'Send' button for the user.
      setSendDisabled(false);
    } else {
      // Requirements missing: Keep the 'Send' button disabled to prevent partial submissions.
      setSendDisabled(true);
    }
  };

  // #endregion

  // #region Event handlers

  /**
   * Closes the help documentation dialog.
   * This handler resets the open state to false, typically triggered by 
   * clicking the 'Close' button, pressing the Escape key, or clicking the backdrop.
   */
  const handleHelpClose = () => {
    // Update the state to unmount or hide the help modal from the view.
    setHelpOpen(false);
  };

  /**
   * Updates the local email message state when the Rich Text Editor content changes.
   * This utilizes a functional update to preserve existing properties (like subject)
   * while synchronizing the latest HTML body content.
   * * @param content - The raw HTML string emitted from the TinyMCE editor.
   */
  const handleEditorChange = (content: string) => {
    // Update state using the spread operator to ensure immutability.
    setEmailMessage({
      ...emailMessage,        // Persist existing fields (e.g., subject, recipient metadata)
      'htmlContent': content  // Overwrite with the latest editor markup
    });
  };

  /**
   * Synchronizes text input changes with the email message state.
   * This handler uses dynamic property keys to update the specific field 
   * (e.g., 'subject') while maintaining the rest of the object's integrity.
   * * @param event - The change event from a standard React controlled input.
   */
  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Use a functional state update to ensure we are working with the most recent state version.
    setEmailMessage(emailMessage => {
      return {
        ...emailMessage,
        // 1. Spread existing state to preserve other fields (like htmlContent).
        // 2. Use computed property name [event.target.name] to target the specific field.
        [event.target.name]: event.target.value
      };
    });
  };

  React.useEffect(() => {
    validateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailMessage]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={1}>
          <Tooltip title="Clear all email content">
            <IconButton onClick={clearEmail} size="large">
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid size={1}>
          <Tooltip title="Email help">
            <IconButton
              onClick={() => setHelpOpen(true)}
              size="large">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid size={7}>
          <Typography variant="button" >{`Receipients: ${props.ids.length} members`}</Typography>
        </Grid>
      </Grid>
      <Grid container size={12}>
        <TextField
          name="subject"
          value={emailMessage.subject}
          autoFocus
          label="Subject"
          sx={styles.subject}
          onChange={handleSubjectChange}
        />
      </Grid>
      <Grid container sx={styles.editor}>
        <RichTextEditor
          initialValue={initialContent}
          onChange={handleEditorChange}
        />
      </Grid>
      <Grid container size={12} justifyContent="flex-end">
        <Grid>
          {working &&
            <CircularProgress size="30" />
          }
        </Grid>
        <Grid>
          <Button
            disabled={sendDisabled}
            endIcon={<SendIcon />}
            onClick={sendEmail}
            size="medium"
            variant="contained"
          >
            Send
          </Button>
        </Grid>
      </Grid>
      <EmailFormHelpDialog show={helpOpen} handleClose={handleHelpClose} />
    </Box>
  );

}

export default EmailForm;